import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useEnviarMensagemAgente } from "@/hooks/use-agent";
import { PropostaCard } from "@/components/manejo/proposta-card";
import { PropostaProgramaCard } from "@/components/manejo/proposta-programa-card";
import type { PropostaManejo, PropostaProgramaEmLote } from "@shared/manejo-proposta";
import { Loader2, Send } from "lucide-react";

// Chat com o Rizoma Agent — Fase A0 (Rizoma_Agente_Whatsapp_Arquitetura.md §6.1).
// O agente nasce aqui, dentro do app, sem nenhuma restrição de WhatsApp (template,
// janela de 24h, teto de membros). A entrada de texto livre (colar tabela / frase
// de um setor) é só mais uma mensagem — o orquestrador (server/agent/orchestrator.ts)
// roteia pra capacidade certa (consulta, ação composta, ou o interpretador de texto
// livre que já existia).
//
// Etapa 1 / Ciclo 1: este componente MUDOU DE ENDEREÇO, não de comportamento —
// era a tela `/manejo` (Comando) e agora é a aba Conversa de `/agente`. Mesmos
// endpoints `/api/agent/*`, mesmo fluxo de proposta/confirmação. O agente virou
// superfície própria porque deixou de ser só do Manejo (o boletim atravessa
// Cacho, Equipe e Manejo).

type Mensagem =
  | { id: string; role: "user"; texto: string }
  | { id: string; role: "agent"; tipo: "texto"; texto: string }
  | { id: string; role: "agent"; tipo: "proposta"; proposta: PropostaManejo }
  | { id: string; role: "agent"; tipo: "proposta_programa_lote"; propostas: PropostaProgramaEmLote[] };

function novoId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random());
}

const MENSAGEM_AGENTE_INDISPONIVEL = "O agente está temporariamente indisponível. Já avisamos o Hugo — tente de novo em alguns minutos.";
const MENSAGEM_ERRO_GENERICO = "Não consegui processar essa mensagem agora.";

// apiRequest lança Error("<status>: <corpo cru da resposta>") — pra erros de
// configuração/infra do agente (503 = GEMINI_API_KEY ausente, 502 = Gemini
// rejeitou a chamada, ex.: modelo descontinuado) isso vaza JSON técnico pro
// operador de campo se a gente só repassar e.message. A bolha do chat (fica
// gravada na conversa) sempre mostra uma frase curta e humana; o toast (some
// sozinho) pode levar o detalhe técnico, é só pra quem for depurar na hora.
function ehErroAgenteIndisponivel(e: unknown): boolean {
  const raw = e instanceof Error ? e.message : String(e);
  const status = parseInt(raw.slice(0, 3), 10);
  return status === 502 || status === 503;
}

export function ConversaAgente() {
  const { toast } = useToast();
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [texto, setTexto] = useState("");
  const enviarMensagem = useEnviarMensagemAgente();
  const fimRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [mensagens.length, enviarMensagem.isPending]);

  const handleEnviar = async () => {
    const textoEnviado = texto.trim();
    if (!textoEnviado || enviarMensagem.isPending) return;
    setTexto("");
    setMensagens(m => [...m, { id: novoId(), role: "user", texto: textoEnviado }]);

    try {
      const resposta = await enviarMensagem.mutateAsync(textoEnviado);
      if (resposta.tipo === "texto") {
        setMensagens(m => [...m, { id: novoId(), role: "agent", tipo: "texto", texto: resposta.texto }]);
      } else if (resposta.tipo === "proposta") {
        setMensagens(m => [...m, { id: novoId(), role: "agent", tipo: "proposta", proposta: resposta.proposta }]);
      } else {
        setMensagens(m => [...m, { id: novoId(), role: "agent", tipo: "proposta_programa_lote", propostas: resposta.propostas }]);
      }
    } catch (e) {
      const indisponivel = ehErroAgenteIndisponivel(e);
      const bolhaTexto = indisponivel ? MENSAGEM_AGENTE_INDISPONIVEL : MENSAGEM_ERRO_GENERICO;
      toast({
        title: "Erro ao falar com o agente",
        description: indisponivel ? MENSAGEM_AGENTE_INDISPONIVEL : (e instanceof Error ? e.message : String(e)),
        variant: "destructive",
      });
      setMensagens(m => [...m, { id: novoId(), role: "agent", tipo: "texto", texto: bolhaTexto }]);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  };

  return (
    <div className="flex flex-col h-[calc(100dvh-14rem)] min-h-[24rem]">
      <p className="text-sm text-muted-foreground shrink-0">
        Converse com o agente: pergunte pelo programa, peça pra gerar o plano da semana, ou cole uma tabela de fertirrigação.
      </p>

      <div className="flex-1 overflow-y-auto space-y-3 py-4 min-h-0">
        {mensagens.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            Comece perguntando <em>"qual o programa do setor X"</em> ou <em>"cria as tarefas de fertirrigação dessa semana"</em>.
          </p>
        )}
        {mensagens.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            {msg.role === "user" ? (
              <div className="max-w-[85%] rounded-lg bg-primary text-primary-foreground px-3 py-2 text-sm whitespace-pre-wrap break-words">
                {msg.texto}
              </div>
            ) : msg.tipo === "texto" ? (
              <div className="max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm whitespace-pre-wrap break-words">
                {msg.texto}
              </div>
            ) : msg.tipo === "proposta" ? (
              <div className="w-full">
                <PropostaCard proposta={msg.proposta} />
              </div>
            ) : (
              <div className="w-full space-y-2">
                {msg.propostas.map((p, idx) => (
                  <PropostaProgramaCard key={idx} proposta={p} />
                ))}
              </div>
            )}
          </div>
        ))}
        {enviarMensagem.isPending && (
          <div className="flex justify-start">
            <div className="rounded-lg bg-muted px-3 py-2 text-sm flex items-center gap-1.5 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Pensando...
            </div>
          </div>
        )}
        <div ref={fimRef} />
      </div>

      <div className="flex items-end gap-2 pt-2 border-t shrink-0">
        <Textarea
          value={texto}
          onChange={e => setTexto(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder='Ex.: "qual o programa do A1?" ou cole a tabela da semana...'
          rows={2}
          className="font-mono text-sm resize-none"
        />
        <Button
          onClick={handleEnviar}
          disabled={!texto.trim() || enviarMensagem.isPending}
          size="icon"
          className="h-10 w-10 shrink-0"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
